import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import DescriptionTwoToneIcon from "@mui/icons-material/DescriptionTwoTone";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import getDescription from "./getDescription";
import References from "./References";
import EmptyItem from "../../Components/EmptyItem";
import importQuestions from "../../Editor/importQuestions";
import CustomTreeView from "../../Interaction/CustomTreeView";
import useClasses from "../../useClasses";

const styles = (theme) => ({
  tag: {
    display: "flex",
    justifyContent: "left",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(0.5),
    },
  },
});

function About({ about, questionList }) {
  const classes = useClasses(styles);
  const { t } = useTranslation();
  const {
    questions,
    description: { description, descriptionItems, tags, usefulFor, references },
  } = importQuestions(questionList);
  const [showItems, setShowItems] = React.useState(description === "");

  const handleShowItems = () => setShowItems((prev) => !prev);

  if (!questions?.length)
    return (
      <EmptyItem
        text={t("timeline.placeholders.emptyDescription")}
        Icon={DescriptionTwoToneIcon}
      />
    );
  return (
    <React.Fragment>
      {description !== "" ? (
        <div style={{ padding: 16, wordBreak: "break-word" }}>
          <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
            {t("interactions.description")}
          </Typography>
          <Typography variant="body2">{description}</Typography>
        </div>
      ) : null}

      {descriptionItems !== "" ? (
        <div style={{ padding: 16 }}>
          <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
            {t("interactions.descriptionItems")}
          </Typography>
          <Typography variant="body2">{descriptionItems}</Typography>
        </div>
      ) : null}

      {questions.length > 0 ? (
        <div style={{ padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
              {t("interactions.items")}
            </Typography>
            <Tooltip
              title={
                showItems ? t("common.buttons.less") : t("common.buttons.more")
              }
            >
              <IconButton
                onClick={handleShowItems}
                style={{ padding: 8 }}
                size="small"
              >
                {showItems ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={t("common.buttons.edit")}>
              <IconButton
                component={Link}
                to={{
                  pathname: "/editor",
                  state: { interactionId: "new", interaction: questionList },
                }}
                style={{ padding: 8 }}
                size="small"
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>

          {showItems ? (
            <CustomTreeView questions={questions} variant="body2" />
          ) : null}
        </div>
      ) : null}

      <div style={{ display: "inline-flex" }}>
        {tags.length !== 0 ? (
          <div style={{ padding: 16 }}>
            <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
              {t("interactions.tags")}
            </Typography>
            <div className={classes.tag}>
              {tags.map(({ id, label }, index) => (
                <Chip key={index} label={label} variant="outlined" />
              ))}
            </div>
          </div>
        ) : null}

        {usefulFor.length !== 0 ? (
          <div style={{ padding: 16 }}>
            <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
              {t("interactions.useful")}
            </Typography>
            <div className={classes.tag}>
              {usefulFor.map(({ id, label }, index) => (
                <Chip key={index} label={label} variant="outlined" />
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {references.length !== 0 ? (
        <div style={{ padding: 16 }}>
          <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
            {t("interactions.references")}
          </Typography>
          <div className={classes.tag}>
            {references.map((reference, index) => (
              <References key={index} reference={reference} />
            ))}
          </div>
        </div>
      ) : null}
    </React.Fragment>
  );
}

export default About;
